import React, { useState } from "react";
import { useToast } from "../../context/ToastContext";
import { apiClient } from "../../utils/http";
import PasswordFields from "./PasswordFields";
import { checkPasswords, reportError } from "./formErrors";

const initialForm = (user) => ({
  name: user ? user.name : "",
  username: user ? user.username : "",
  email: user ? user.email : "",
  contact_number: user ? user.contact_number || "" : "",
  active: user ? user.active : true,
  password: "",
  password_confirmation: "",
});

const REQUIRED = [
  ["name", "Please enter the user's name"],
  ["username", "Please enter a username"],
  ["email", "Please enter an email address"],
];

const validate = (form, isNew) => {
  const missing = REQUIRED.find(([field]) => !String(form[field]).trim());
  if (missing) return { [missing[0]]: missing[1] };
  if (/\s/.test(form.username.trim())) return { username: "The username can't contain spaces" };
  return isNew ? checkPasswords(form) : null;
};

/* Add a Job Order user (with a first password) or edit one's details. */
export default function UserFormModal({ user, onClose, onSaved }) {
  const showToast = useToast();
  const [form, setForm] = useState(() => initialForm(user));
  const [saving, setSaving] = useState(false);

  const update = (patch) => setForm((current) => ({ ...current, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    const errors = validate(form, !user);
    if (errors) {
      reportError({ errors }, showToast, "user");
      return;
    }

    const payload = {
      name: form.name.trim(),
      username: form.username.trim(),
      email: form.email.trim(),
      contact_number: form.contact_number.trim() || null,
      active: form.active,
    };
    if (!user) {
      payload.password = form.password;
      payload.password_confirmation = form.password_confirmation;
    }

    setSaving(true);
    try {
      const res = await apiClient.post(user ? `users/update/${user.id}` : "users/add", payload);
      showToast(res.data.message);
      onSaved(res.data.user);
    } catch (err) {
      setSaving(false);
      reportError(err, showToast, "user");
    }
  };

  return (
    <div className="modal-backdrop no-print" style={{ zIndex: 1005 }}>
      <div className="modal-container modal-md">
        <div className="modal-header">
          <h3>{user ? "Edit User" : "Add User"}</h3>
          <button type="button" className="btn-icon-close" title="Close" onClick={onClose}>
            &times;
          </button>
        </div>
        <form onSubmit={submit} noValidate>
          <div className="modal-body">
            {user && user.other_access && (
              <div className="users-note">This user also works in other Denzo apps; these details are shared with them.</div>
            )}

            <div className="form-group">
              <label htmlFor="user-name">
                Full Name <span className="req">*</span>
              </label>
              <input type="text" id="user-name" className="form-control" maxLength={255} value={form.name} onChange={(e) => update({ name: e.target.value })} />
            </div>

            <div className="form-grid grid-2">
              <div className="form-group">
                <label htmlFor="user-username">
                  Username <span className="req">*</span>
                </label>
                <input
                  type="text"
                  id="user-username"
                  className="form-control"
                  maxLength={20}
                  autoComplete="off"
                  value={form.username}
                  onChange={(e) => update({ username: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="user-contact_number">Mobile Number</label>
                <input
                  type="tel"
                  id="user-contact_number"
                  className="form-control"
                  maxLength={15}
                  value={form.contact_number}
                  onChange={(e) => update({ contact_number: e.target.value })}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="user-email">
                Email Address <span className="req">*</span>
              </label>
              <input type="email" id="user-email" className="form-control" maxLength={255} value={form.email} onChange={(e) => update({ email: e.target.value })} />
            </div>

            {!user && <PasswordFields idPrefix="user" form={form} onChange={update} label="Password" />}

            <label className="checkbox-label">
              <input type="checkbox" checked={form.active} onChange={(e) => update({ active: e.target.checked })} />
              Active (can sign in)
            </label>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving..." : user ? "Save Changes" : "Add User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
